import React, { useMemo } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails, Box, Typography, Paper, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';

const CORES_RISCO: Record<string, string> = {
  CRITICO: '#E74C3C', ALTO: '#F39C12', MEDIO: '#F1C40F', BAIXO: '#27AE60',
};

const NIVEIS = ['CRITICO', 'ALTO', 'MEDIO', 'BAIXO'] as const;

interface AnaliseAvancada {
  matriz_setor_risco: { setor: string; nivel_risco: string; total: number }[];
  por_regional: { regional: string; nivel_risco: string; total: number }[];
  top_atividades: {
    atividade_principal: string;
    total: number;
    criticos_altos: number;
    pct_risco: number;
  }[];
  ivs_regional: {
    regional: string;
    total_estab: number;
    criticos: number;
    altos: number;
    medios: number;
    baixos: number;
    vencidos: number;
    medidas: number;
    irregulares: number;
    ivs: number;
  }[];
}

interface AnaliseRiscoSectionProps {
  analise: AnaliseAvancada | null;
}

const ivsColor = (ivs: number) => {
  if (ivs >= 70) return '#E74C3C';
  if (ivs >= 50) return '#F39C12';
  if (ivs >= 30) return '#F1C40F';
  return '#27AE60';
};

const ivsLabel = (ivs: number) => {
  if (ivs >= 70) return 'Critico';
  if (ivs >= 50) return 'Alto';
  if (ivs >= 30) return 'Medio';
  return 'Baixo';
};

const AnaliseRiscoSection: React.FC<AnaliseRiscoSectionProps> = ({ analise }) => {
  const matriz = useMemo(() => {
    if (!analise?.matriz_setor_risco) return { setores: [], dados: {} as Record<string, Record<string, number>>, max: 0 };
    const dados: Record<string, Record<string, number>> = {};
    let max = 0;
    analise.matriz_setor_risco.forEach(r => {
      if (!dados[r.setor]) dados[r.setor] = {};
      const total = Number(r.total) || 0;
      dados[r.setor][r.nivel_risco] = total;
      if (total > max) max = total;
    });
    const setores = Object.keys(dados).sort();
    return { setores, dados, max };
  }, [analise]);

  const chartRegional = useMemo(() => {
    if (!analise?.por_regional) return [];
    const map: Record<string, Record<string, number>> = {};
    analise.por_regional.forEach(r => {
      if (!map[r.regional]) map[r.regional] = {};
      map[r.regional][r.nivel_risco] = Number(r.total) || 0;
    });
    return Object.entries(map)
      .map(([regional, riscos]) => ({ regional, ...riscos }))
      .sort((a: any, b: any) => {
        const ta = NIVEIS.reduce((s, n) => s + (Number(a[n]) || 0), 0);
        const tb = NIVEIS.reduce((s, n) => s + (Number(b[n]) || 0), 0);
        return tb - ta;
      });
  }, [analise]);

  const topAtividades = useMemo(() => {
    return (analise?.top_atividades || []).map(a => ({
      ...a,
      pct_risco: Number(a.pct_risco) || 0,
      total: Number(a.total) || 0,
      criticos_altos: Number(a.criticos_altos) || 0,
    }));
  }, [analise]);

  const ivsList = useMemo(() => (analise?.ivs_regional || []).map(r => ({
    ...r,
    ivs: Number(r.ivs) || 0,
    total_estab: Number(r.total_estab) || 0,
    criticos: Number(r.criticos) || 0,
    altos: Number(r.altos) || 0,
    medios: Number(r.medios) || 0,
    baixos: Number(r.baixos) || 0,
    vencidos: Number(r.vencidos) || 0,
    medidas: Number(r.medidas) || 0,
    irregulares: Number(r.irregulares) || 0,
  })), [analise]);

  const cellBg = (count: number, max: number, nivel: string): string => {
    if (!count || !max) return '#FAFAFA';
    const intensity = Math.min(1, count / max);
    const base = CORES_RISCO[nivel] || '#999';
    return base + Math.round(intensity * 200 + 40).toString(16).padStart(2, '0');
  };

  return (
    <Accordion defaultExpanded sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', '&:before': { display: 'none' } }} elevation={1}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AnalyticsIcon color="primary" />
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>Analise de Risco — Estratificacao Avancada</Typography>
            <Typography variant="caption" color="text.secondary">
              Setor x Risco, distribuicao por regional, atividades criticas e Indice de Vulnerabilidade Sanitaria
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {/* Matriz Setor x Risco */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Matriz Setor x Risco
              </Typography>
              <TableContainer sx={{ maxHeight: 320 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#fafafa' }}>Setor</TableCell>
                      {NIVEIS.map(n => (
                        <TableCell key={n} align="center" sx={{ fontWeight: 700, bgcolor: '#fafafa' }}>
                          <Chip label={n} size="small" sx={{ bgcolor: CORES_RISCO[n], color: '#fff', fontSize: 10 }} />
                        </TableCell>
                      ))}
                      <TableCell align="center" sx={{ fontWeight: 700, bgcolor: '#fafafa' }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {matriz.setores.map(setor => {
                      const row = matriz.dados[setor];
                      const total = NIVEIS.reduce((s, n) => s + (row[n] || 0), 0);
                      return (
                        <TableRow key={setor}>
                          <TableCell sx={{ fontWeight: 500 }}>{setor}</TableCell>
                          {NIVEIS.map(n => (
                            <TableCell key={n} align="center"
                              sx={{
                                bgcolor: cellBg(row[n] || 0, matriz.max, n),
                                color: (row[n] || 0) > matriz.max * 0.5 ? '#fff' : 'inherit',
                                fontWeight: (row[n] || 0) > 0 ? 600 : 400,
                              }}>
                              {row[n] || 0}
                            </TableCell>
                          ))}
                          <TableCell align="center" sx={{ fontWeight: 700 }}>{total}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Bar Chart por Regional */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Estratificacao por Regional Administrativa
              </Typography>
              <ResponsiveContainer width="100%" height={310}>
                <BarChart data={chartRegional} margin={{ top: 5, right: 5, bottom: 35, left: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="regional" fontSize={10} angle={-25} textAnchor="end" interval={0} height={60} />
                  <YAxis fontSize={11} />
                  <RTooltip />
                  <Legend />
                  <Bar dataKey="CRITICO" stackId="a" fill={CORES_RISCO.CRITICO} name="Critico" />
                  <Bar dataKey="ALTO"    stackId="a" fill={CORES_RISCO.ALTO}    name="Alto" />
                  <Bar dataKey="MEDIO"   stackId="a" fill={CORES_RISCO.MEDIO}   name="Medio" />
                  <Bar dataKey="BAIXO"   stackId="a" fill={CORES_RISCO.BAIXO}   name="Baixo" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Top atividades */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Top Atividades por % de Risco (Critico + Alto)
              </Typography>
              <ResponsiveContainer width="100%" height={310}>
                <BarChart data={topAtividades} layout="vertical" margin={{ left: 10, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} unit="%" fontSize={11} />
                  <YAxis dataKey="atividade_principal" type="category" width={180} fontSize={10} />
                  <RTooltip formatter={(value: any) => `${value}%`} />
                  <Bar dataKey="pct_risco" name="% Critico/Alto" radius={[0, 4, 4, 0]}>
                    {topAtividades.map((a, i) => (
                      <Cell key={i} fill={a.pct_risco >= 60 ? CORES_RISCO.CRITICO : a.pct_risco >= 40 ? CORES_RISCO.ALTO : CORES_RISCO.MEDIO} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* IVS por regional */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Indice de Vulnerabilidade Sanitaria por Regional
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Composicao: nivel de risco + alvaras vencidos + medidas cautelares (0 a 100)
              </Typography>
              <Grid container spacing={1}>
                {ivsList.map(r => (
                  <Grid item xs={6} key={r.regional}>
                    <Tooltip title={
                      <Box sx={{ fontSize: 11 }}>
                        <div><b>{r.regional}</b></div>
                        <div>Estabelecimentos: {r.total_estab}</div>
                        <div>Critico: {r.criticos} • Alto: {r.altos}</div>
                        <div>Medio: {r.medios} • Baixo: {r.baixos}</div>
                        <div>Alvaras vencidos: {r.vencidos}</div>
                        <div>Medidas cautelares: {r.medidas}</div>
                        <div>Irregulares/Interditados: {r.irregulares}</div>
                      </Box>
                    } arrow>
                      <Paper variant="outlined" sx={{
                        p: 1.2, borderLeft: `4px solid ${ivsColor(r.ivs)}`, cursor: 'help',
                        '&:hover': { boxShadow: 2 },
                      }}>
                        <Typography variant="caption" color="text.secondary">{r.regional}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                          <Typography variant="h5" fontWeight={800} sx={{ color: ivsColor(r.ivs) }}>
                            {r.ivs.toFixed(0)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: ivsColor(r.ivs), fontWeight: 600 }}>
                            {ivsLabel(r.ivs)}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {r.total_estab} estab • {r.irregulares} irreg
                        </Typography>
                      </Paper>
                    </Tooltip>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default AnaliseRiscoSection;
